import { computed, toValue, type ComputedRef, type MaybeRefOrGetter } from 'vue'
import {
  mdiAccountPlus,
  mdiAccountRemove,
  mdiAccountSwitch,
  mdiHistory,
  mdiLockOpenVariant,
  mdiMapMarkerRadiusOutline,
  mdiPencil,
  mdiUpload
} from '@mdi/js'

import { POST_STATION_LOCK_BANNER_TEXT } from '@/modules/company/PostStation/constants'

/**
 * Ações do Detalhes do Plantão: menu flutuante no desktop, dock + ActionSheet no
 * celular (spec 2026-09-30 §5 R6 e §9).
 *
 * UMA fonte para as condições de visibilidade, habilitação e motivo de cada ação.
 * Antes elas viviam repetidas em quatro blocos do template do `stationDetailsModal`
 * (menu flutuante e barra mobile, com e sem smart allocation). O desktop mantém o
 * markup próprio e só lê daqui o que mostrar e se trava; o dock do celular monta a
 * interface a partir da lista.
 *
 * Ordem da lista = ordem do menu flutuante do desktop (Q35): a primária, "Enviar
 * registro" em SEGUNDO, os botões avulsos (cadeado, tentativas) e os itens dos três
 * pontinhos.
 */

export type StationDetailActionId =
  | 'allocate'
  | 'edit'
  | 'send-record'
  | 'exceptionality'
  | 'check-attempts'
  | 'history'
  | 'remove'

/**
 * `primary`/`secondary`: as duas ações do dock. `standalone`: botão avulso do menu
 * flutuante. `overflow`: item dos três pontinhos do desktop.
 */
export type StationDetailActionPlacement = 'primary' | 'secondary' | 'standalone' | 'overflow'

/** `none`: sem plantão carregado ou fora do dashboard (Aprovações) — não há menu. */
export type StationDetailActionsVariant = 'smart' | 'legacy' | 'none'

export interface StationDetailAction {
  id: StationDetailActionId
  label: string
  /** Subtítulo do item no ActionSheet quando a ação está habilitada. */
  description?: string
  /** Path do `@mdi/js`. */
  icon: string
  placement: StationDetailActionPlacement
  primary?: boolean
  danger?: boolean
  /** Sidebar ou seção que a ação abre está aberta agora. */
  active?: boolean
  disabled: boolean
  /** Por que está desabilitada — tooltip no desktop, texto visível no celular. */
  disabledReason?: string
  handler: () => void
}

type Flag = MaybeRefOrGetter<boolean>

export interface StationDetailActionsSource {
  variant: MaybeRefOrGetter<StationDetailActionsVariant>
  hasDoctor: Flag
  isUnidentified: Flag
  /** `doctorStation.ourTeam` — gate do cadeado na variante smart. */
  ourTeam: Flag
  canAllocateDoctor: Flag
  canEditStation: Flag
  canRemoveDoctor: Flag
  canReadHistory: Flag
  canReadExceptionalities: Flag
  exceptionalitiesEnabled: Flag
  stationHistoryEnabled: Flag
  isPostStationLocked: Flag
  /**
   * Trava por perna (`legLock` do registro, backend #3055). Opcionais: sem eles
   * nada trava — o backend continua recusando a mutação.
   */
  isLegLocked?: Flag
  legLockReason?: MaybeRefOrGetter<string | null | undefined>
  canEmitActivityRecord: Flag
  scheduleAcceptsActivityRecords: Flag
  hasStationEnded: Flag
  isExceptionalityOpen: Flag
  isHistoryOpen: Flag
  isCheckAttemptsOpen: Flag
}

export interface StationDetailActionHandlers {
  allocate: () => void
  edit: () => void
  sendRecord: () => void
  exceptionality: () => void
  checkAttempts: () => void
  history: () => void
  remove: () => void
}

export const SEND_RECORD_LABEL = 'Enviar registro'
export const SEND_RECORD_NO_PERMISSION = 'Você não tem permissão para enviar registros.'
export const SEND_RECORD_SCHEDULE_OFF =
  'Esta escala não aceita Registro de Atividade. Ajuste a política de registro.'
export const SEND_RECORD_NOT_ENDED = 'Disponível após o fim do plantão.'

export function useStationDetailActions(
  source: StationDetailActionsSource,
  handlers: StationDetailActionHandlers
) {
  const on = (flag: Flag) => toValue(flag)

  /**
   * RENDERIZAÇÃO, não habilitação. Enquanto a escala não tiver Registro de
   * Atividade ligado — o único cenário possível antes de o backend da F2 subir,
   * porque `registration-settings` responde 404 e a store cai no default seguro —
   * o botão simplesmente NÃO EXISTE (um FAB desabilitado em todo detalhe no dia do
   * deploy é o kill switch meio-ligado que §4.1 manda não repetir). Sem médico
   * identificado não há registro de atividade a enviar (M5-B).
   */
  const showSendRecordAction = computed(
    () => on(source.scheduleAcceptsActivityRecords) && !on(source.isUnidentified)
  )

  /**
   * TRÊS condições independentes (M1, balão ⑦), com mensagens diferentes de
   * propósito: sem a permissão de EMISSÃO não adianta configurar a escala; com a
   * permissão mas a escala recusando, o caminho é a política; com os dois OK falta
   * o plantão ter terminado (Q35) — o médico ainda pode estar trabalhando.
   */
  const isSendRecordDisabled = computed(
    () =>
      !on(source.canEmitActivityRecord) ||
      !on(source.scheduleAcceptsActivityRecords) ||
      !on(source.hasStationEnded)
  )

  const sendRecordTooltip = computed(() => {
    if (!on(source.canEmitActivityRecord)) return SEND_RECORD_NO_PERMISSION
    if (!on(source.scheduleAcceptsActivityRecords)) return SEND_RECORD_SCHEDULE_OFF
    if (!on(source.hasStationEnded)) return SEND_RECORD_NOT_ENDED
    return SEND_RECORD_LABEL
  })

  /** Gate do toggle "⋯" do desktop (linha do `v-if="canReadHistory"` de antes). */
  const isOverflowAvailable = computed(
    () => toValue(source.variant) === 'smart' && on(source.canReadHistory)
  )

  const isLegLocked = () => (source.isLegLocked === undefined ? false : on(source.isLegLocked))
  const legLockReason = () =>
    isLegLocked() ? toValue(source.legLockReason) || undefined : undefined

  /** Pós-Plantão OU perna travada: as ações que trocam/removem o médico desabilitam. */
  const isMutationLocked = () => on(source.isPostStationLocked) || isLegLocked()

  /**
   * "Editar plantão" NÃO trava pela perna: é o único caminho para o
   * `update.station.modal`, que já trava por dentro (banner, horário/valor inertes,
   * Excluir desabilitado) e ainda salva setor/especialidade, que o backend aceita.
   */
  const postStationLockReason = () =>
    on(source.isPostStationLocked) ? POST_STATION_LOCK_BANNER_TEXT : undefined

  // Com as duas travas, o motivo do Pós-Plantão prevalece (é o texto que o banner
  // do detalhe já mostra).
  const lockReason = () =>
    on(source.isPostStationLocked) ? POST_STATION_LOCK_BANNER_TEXT : legLockReason()

  const allocateLabel = () => {
    if (on(source.isUnidentified)) return 'Identificar médico'
    return on(source.hasDoctor) ? 'Alterar médico' : 'Alocar médico'
  }

  const sendRecordAction = (): StationDetailAction => ({
    id: 'send-record',
    label: SEND_RECORD_LABEL,
    icon: mdiUpload,
    placement: 'secondary',
    disabled: isSendRecordDisabled.value,
    disabledReason: isSendRecordDisabled.value ? sendRecordTooltip.value : undefined,
    handler: handlers.sendRecord
  })

  const exceptionalityAction = (): StationDetailAction => ({
    id: 'exceptionality',
    label: 'Excepcionalidade',
    description: 'Ver ou solicitar excepcionalidade',
    icon: mdiLockOpenVariant,
    placement: 'standalone',
    active: on(source.isExceptionalityOpen),
    disabled: false,
    handler: handlers.exceptionality
  })

  const checkAttemptsAction = (): StationDetailAction => ({
    id: 'check-attempts',
    label: 'Tentativas de check',
    description: 'Aceitas e rejeitadas, com mapa',
    icon: mdiMapMarkerRadiusOutline,
    placement: 'standalone',
    active: on(source.isCheckAttemptsOpen),
    disabled: false,
    handler: handlers.checkAttempts
  })

  const historyAction = (placement: StationDetailActionPlacement): StationDetailAction => ({
    id: 'history',
    label: 'Histórico',
    description: 'Quem alterou e quando',
    icon: mdiHistory,
    placement,
    active: on(source.isHistoryOpen),
    disabled: false,
    handler: handlers.history
  })

  const allocateAction = (): StationDetailAction => ({
    id: 'allocate',
    label: allocateLabel(),
    icon: on(source.hasDoctor) ? mdiAccountSwitch : mdiAccountPlus,
    placement: 'primary',
    primary: true,
    disabled: isMutationLocked(),
    disabledReason: lockReason(),
    handler: handlers.allocate
  })

  // Não trava (nem Pós-Plantão nem perna): abre o `update.station.modal`, que já
  // trava internamente.
  const editOverflowAction = (): StationDetailAction => ({
    id: 'edit',
    label: 'Editar plantão',
    description: 'Horário, posição, setor, especialidade',
    icon: mdiPencil,
    placement: 'overflow',
    disabled: false,
    disabledReason: undefined,
    handler: handlers.edit
  })

  const removeAction = (): StationDetailAction => ({
    id: 'remove',
    label: 'Remover médico',
    description: 'O plantão volta a ser vaga',
    icon: mdiAccountRemove,
    placement: 'overflow',
    danger: true,
    disabled: isMutationLocked(),
    disabledReason: lockReason(),
    handler: handlers.remove
  })

  const showSmartExceptionality = () =>
    on(source.exceptionalitiesEnabled) && on(source.canReadExceptionalities) && on(source.ourTeam)

  function smartActions(): StationDetailAction[] {
    const list: StationDetailAction[] = []
    if (on(source.canAllocateDoctor)) list.push(allocateAction())
    if (showSendRecordAction.value) list.push(sendRecordAction())
    if (showSmartExceptionality()) list.push(exceptionalityAction())
    list.push(checkAttemptsAction())
    if (on(source.canEditStation)) list.push(editOverflowAction())
    if (on(source.hasDoctor) && on(source.canRemoveDoctor)) list.push(removeAction())
    if (on(source.stationHistoryEnabled) && on(source.canReadHistory)) {
      list.push(historyAction('overflow'))
    }
    return list
  }

  // Variante sem smart allocation: o lápis é a primária (e trava — RN-07), e
  // cadeado/histórico não checam permissão nem flag de histórico, como hoje.
  function legacyActions(): StationDetailAction[] {
    const list: StationDetailAction[] = [
      {
        id: 'edit',
        label: 'Editar plantão',
        icon: mdiPencil,
        placement: 'primary',
        primary: true,
        disabled: on(source.isPostStationLocked),
        disabledReason: postStationLockReason(),
        handler: handlers.edit
      }
    ]
    if (showSendRecordAction.value) list.push(sendRecordAction())
    if (on(source.exceptionalitiesEnabled)) list.push(exceptionalityAction())
    list.push(checkAttemptsAction())
    list.push(historyAction('standalone'))
    return list
  }

  const actions: ComputedRef<StationDetailAction[]> = computed(() => {
    switch (toValue(source.variant)) {
      case 'smart':
        return smartActions()
      case 'legacy':
        return legacyActions()
      default:
        return []
    }
  })

  const actionMap = computed(
    () =>
      Object.fromEntries(actions.value.map((action) => [action.id, action])) as Partial<
        Record<StationDetailActionId, StationDetailAction>
      >
  )

  return {
    actions,
    actionMap,
    isOverflowAvailable,
    showSendRecordAction,
    isSendRecordDisabled,
    sendRecordTooltip
  }
}
